// One id shared by the browser pixel and the server (Conversions API) so Meta de-duplicates the pair.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { gaEvent, gaPageView } from "./ga";

const STD = ["Lead", "CompleteRegistration", "InitiateCheckout", "SubmitApplication", "Contact", "Schedule", "ViewContent", "Purchase", "PageView"];
const YEAR = 31536000;

export const newMetaEventId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const readCookie = (n) => { const m = document.cookie.match(`(?:^|; )${n}=([^;]*)`); return m ? decodeURIComponent(m[1]) : null; };
const writeCookie = (n, v) => {
  const dom = /(^|\.)hellomortgage\.ca$/.test(window.location.hostname) ? ";domain=.hellomortgage.ca" : "";
  document.cookie = `${n}=${encodeURIComponent(v)};path=/;max-age=${YEAR};SameSite=Lax${dom}${window.location.protocol === "https:" ? ";Secure" : ""}`;
};

// Stable first-party visitor id (index.html creates it before the pixel inits; this is the React-side reader).
export const getVisitorId = () => {
  try {
    let vid = window.__metaVid || readCookie("_hm_vid") || localStorage.getItem("_hm_vid");
    if (!vid) { vid = newMetaEventId(); writeCookie("_hm_vid", vid); localStorage.setItem("_hm_vid", vid); window.__metaVid = vid; }
    return vid;
  } catch { return null; }
};

// Last-touch ad attribution (utm_* + fbclid + gclid) remembered for 30 days so a lead captured several pages later still credits the ad.
const ATTR_KEY = "_hm_attr";
const TEST_KEY = "_hm_test";
export const captureAttribution = () => {
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get("test") === "1") sessionStorage.setItem(TEST_KEY, "1");  // hidden switch: flags every submission in this tab as a test record
    const utm = Object.fromEntries([...q].filter(([k, v]) => k.startsWith("utm_") && v));
    const fbclid = q.get("fbclid");
    const gclid = q.get("gclid");
    if (!fbclid && !gclid && !Object.keys(utm).length) return;
    const prev = getAttribution();
    const next = { utm: Object.keys(utm).length ? utm : prev.utm || {}, fbclid: fbclid || prev.fbclid || null, gclid: gclid || prev.gclid || null, landing_url: window.location.href, ts: Date.now() };
    localStorage.setItem(ATTR_KEY, JSON.stringify(next));
  } catch { /* storage blocked */ }
};
// Same record captureAttribution writes, for taps that open an in-page widget instead of navigating to a tagged URL.
export const setAttribution = (utm) => {
  try {
    const prev = getAttribution();
    localStorage.setItem(ATTR_KEY, JSON.stringify({ utm: { ...(prev.utm || {}), ...utm }, fbclid: prev.fbclid || null, gclid: prev.gclid || null, landing_url: prev.landing_url || window.location.href, ts: Date.now() }));
  } catch { /* storage blocked */ }
};
export const isTestSession = () => { try { return sessionStorage.getItem(TEST_KEY) === "1" || new URLSearchParams(window.location.search).get("test") === "1"; } catch { return false; } };
// Flat payload every lead form sends: utm_*, fbclid, gclid, landing_url + the pixel cookies for server-side matching.
export const attributionPayload = () => {
  const a = getAttribution();
  const q = new URLSearchParams(window.location.search);
  return { ...(a.utm || {}), ...Object.fromEntries([...q].filter(([k, v]) => k.startsWith("utm_") && v)),
    fbclid: q.get("fbclid") || a.fbclid || null, gclid: q.get("gclid") || a.gclid || null, landing_url: a.landing_url || window.location.href,
    first_touch_at: a.ts ? new Date(a.ts).toISOString() : null, ...getMetaCookies() };
};
export const getAttribution = () => {
  try {
    const a = JSON.parse(localStorage.getItem(ATTR_KEY) || "null");
    if (!a || Date.now() - (a.ts || 0) > 30 * 86400000) return {};
    return a;
  } catch { return {}; }
};
export const getMetaCookies = () => ({ fbp: readCookie("_fbp"), fbc: readCookie("_fbc") });

export const pixelTrack = (event, params = {}, eventId) => {
  if (isTestSession()) return;  // staff/test sessions never pollute Meta or GA conversions
  if (event !== "PageView") gaEvent(event, params); // GA4 mirror of every conversion event
  try {
    if (!window.fbq) return;
    window.fbq(STD.includes(event) ? "track" : "trackCustom", event, params, eventId ? { eventID: eventId } : undefined);
  } catch { /* pixel blocked */ }
};

// Server mirror for events whose conversion endpoint doesn't already send them (PageView, InitiateCheckout).
export const serverTrack = (event, eventId, params) => {
  try {
    const body = JSON.stringify({ event_name: event, event_id: eventId, url: window.location.href, custom_data: params || null, external_id: getVisitorId() });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/meta/event`;
    if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    else fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true, credentials: "include" }).catch(() => {});
  } catch { /* never block UI */ }
};

// Pixel + server pair in one call.
export const trackBoth = (event, params = {}) => {
  const id = newMetaEventId();
  pixelTrack(event, params, id);
  serverTrack(event, id, params);
  return id;
};

// Meta-normalised SHA-256 (same rules the server uses) so browser + server hashes line up.
const sha256 = async (v) => {
  const buf = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(v));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
};
const norm = {
  em: (v) => v.trim().toLowerCase(),
  ph: (v) => { const d = v.replace(/\D/g, ""); return d.length === 10 ? `1${d}` : d; },
  fn: (v) => v.trim().toLowerCase().replace(/[^\p{L}\p{N}_]/gu, ""),
  ln: (v) => v.trim().toLowerCase().replace(/[^\p{L}\p{N}_]/gu, ""),
};

// Remember who this browser belongs to (hashed) after a lead form or portal sign-in. Picked up by the server mirror via the
// `_hm_kv` cookie and by the pixel's Advanced Matching on the next page load.
export const rememberVisitor = async ({ email, phone, firstName, lastName, accountId } = {}) => {
  try {
    if (!window.crypto?.subtle) return;
    let kv = {};
    try { kv = JSON.parse(localStorage.getItem("_hm_kv") || "{}") || {}; } catch { kv = {}; }
    const pairs = [["em", email], ["ph", phone], ["fn", firstName], ["ln", lastName]];
    for (const [k, raw] of pairs) {
      const v = raw && norm[k](String(raw));
      if (v) kv[k] = await sha256(v);
    }
    if (accountId) kv.eid = String(accountId);
    const json = JSON.stringify(kv);
    localStorage.setItem("_hm_kv", json);
    writeCookie("_hm_kv", json);
  } catch { /* storage blocked */ }
};

// Mirrors PageView server-side on first load (reusing the id index.html gave the pixel) and on every SPA route change.
let lastPageKey = null;
export const useMetaPageView = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    const key = pathname + search;
    if (pathname.startsWith("/admin") || key === lastPageKey) return;
    captureAttribution();
    const isFirst = lastPageKey === null;
    lastPageKey = key;
    gaPageView(key);
    if (isFirst) {
      if (window.__metaPV) serverTrack("PageView", window.__metaPV);
      return;
    }
    trackBoth("PageView");
  }, [pathname, search]);
};
